/* ---------------------------------------------------------- */
/* VideoPlayer Contents                                       */
/* ---------------------------------------------------------- */
/* Table of contents:                                         */
/* 1. Container                                               */
/* 2. Player                                                  */
/* 2. Center Play Button                                      */
/* 4. Title                                                   */
/* 5. Controls                                                */
/* 6. Range Element                                           */
/* 7. Tooltip                                                 */
/* 8. Captions                                                */
/* ---------------------------------------------------------- */

/* ---------------------------------------------------------- */
/* 1. Container                                               */
/* ---------------------------------------------------------- */

.VideoPlayer__container {
  width: 100%;
  border-radius: var(--brand-borderRadius-medium);
  overflow: hidden;
  background-color: var(--base-color-scale-black-0);
  display: flex;
  flex-direction: column;
}

.VideoPlayer__overlayContainer {
  flex: 1;
  position: relative;
  height: 100%;
}

@media (forced-colors: active) {
  .VideoPlayer__container {
    border: var(--brand-borderWidth-thin) solid ButtonBorder;
  }
}

/* ---------------------------------------------------------- */
/* 2. Player                                                  */
/* ---------------------------------------------------------- */

.VideoPlayer {
  /* Private variable used for internal calculations */
  --brand-VideoPlayer-controls-min-height: 56px;
  width: 100%;
  max-height: calc(100vh - var(--brand-VideoPlayer-controls-min-height));
  margin: 0 auto;
  display: block;
}

.VideoPlayer__container--fullscreen .VideoPlayer {
  position: absolute;
  height: 100%;
}

/* ---------------------------------------------------------- */
/* 3. Center Play Button                                      */
/* ---------------------------------------------------------- */

.VideoPlayer__playButtonOverlay {
  font: inherit;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #00000033; /* TODO Use a token for this once v8 migration has been completed */
  border: none;
  z-index: 1;
}

.VideoPlayer__playButtonOverlay svg {
  position: relative;
  top: calc(var(--brand-VideoPlayer-controls-min-height) / 2);
}

.VideoPlayer__playButtonOverlay.VideoPlayer__playButtonOverlay--transparent {
  background: transparent;
}

.VideoPlayer__playButtonOverlay:focus-visible {
  outline: var(--brand-borderWidth-thicker) solid var(--brand-color-focus);
  outline-offset: var(--base-size-2);
}

.VideoPlayer__playButton {
  width: 25%;
  height: 25%;
  max-width: var(--brand-VideoPlayer-playButton-width);
  max-height: var(--brand-VideoPlayer-playButton-height);
  opacity: 0.8;
  color: var(--brand-videoPlayer-playButton-fgColor-rest);
}

@media (forced-colors: active) {
  .VideoPlayer__playButton {
    color: Highlight;
    opacity: 1;
  }
}

/* ---------------------------------------------------------- */
/* 4. Title                                                   */
/* ---------------------------------------------------------- */

.VideoPlayer__title {
  transition: var(--brand-VideoPlayer-transition);
  top: 0;
  position: absolute;
  left: 0;
  width: 100%;
  z-index: 2;
  padding: var(--base-size-16);
  color: var(--brand-videoPlayer-title-fgColor);
  display: grid;
  align-items: center;
  justify-content: flex-start;
  grid-gap: var(--base-size-12);
  grid-template-columns: auto auto;
  background: var(--brand-videoPlayer-title-bgColor);
  transition: all var(--brand-animation-duration-fast) var(--brand-animation-easing-default);
}

.VideoPlayer__title.VideoPlayer__title--hidden {
  opacity: 0;
  visibility: hidden;
}

@media (forced-colors: active) {
  .VideoPlayer__title {
    background: Canvas;
    color: CanvasText;
  }
}

/* ---------------------------------------------------------- */
/* 5. Controls                                                */
/* ---------------------------------------------------------- */

.VideoPlayer__controlsBar {
  min-height: var(--brand-VideoPlayer-controls-min-height);
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
  gap: var(--base-size-16);
  border-bottom-right-radius: var(--brand-borderRadius-medium);
  border-bottom-left-radius: var(--brand-borderRadius-medium);
  width: 100%;
  padding: var(--base-size-12) var(--base-size-16);
  pointer-events: all;
  opacity: 1;
}

.VideoPlayer__controlsBar__row1,
.VideoPlayer__controlsBar__row2 {
  display: flex;
  align-items: center;
  gap: var(--base-size-16);
}

@media (max-width: 34rem) {
  .VideoPlayer__controlsBar {
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    grid-template-rows: auto auto;
    gap: var(--base-size-12);
  }

  .VideoPlayer__controlsBar__row2 {
    justify-self: space-evenly;
    justify-content: space-evenly;
    width: 100%;
  }
}

.VideoPlayer__controls:focus,
.VideoPlayer__controls:focus-within {
  bottom: 0;
}

.VideoPlayer__iconControl {
  font: inherit;
  width: auto;
  background: none;
  border: none;
  cursor: pointer;
  height: var(--base-size-24);
  box-sizing: content-box;
  position: relative;
  padding: 0;
}

.VideoPlayer__iconControl:focus-visible {
  outline: var(--brand-borderWidth-thick) solid var(--brand-color-focus);
  outline-offset: var(--base-size-6);
  opacity: 1;
}

.VideoPlayer__iconControl:not(.VideoPlayer__closedCaption):focus-visible {
  border-radius: var(--brand-borderRadius-small);
}

.VideoPlayer__iconControl svg {
  color: var(--brand-videoPlayer-controls-fgColor);
}

.VideoPlayer__seek {
  flex: 1;
  display: flex;
  align-items: center;
  gap: var(--base-size-16);
}

.VideoPlayer__progressTime {
  color: var(--brand-videoPlayer-controls-fgColor);
}

.VideoPlayer__container--small .VideoPlayer__totalTime {
  display: none;
}

.VideoPlayer__progressBar {
  flex: 1;
  height: var(--base-size-6);
}

.VideoPlayer__closedCaption {
  border-radius: var(--brand-borderRadius-medium);
  padding: 0.125rem var(--base-size-4);
  border: 0.125rem solid var(--brand-videoPlayer-closedCaption-borderColor-enabled);
  background: var(--brand-videoPlayer-closedCaption-bgColor-enabled);
  position: relative;
}

.VideoPlayer__ccOff {
  background-color: transparent;
}

.VideoPlayer__ccText {
  color: var(--brand-videoPlayer-closedCaption-fgColor-enabled);
  line-height: 25px; /* visual alignment */
}

.VideoPlayer__ccOff .VideoPlayer__ccText {
  color: var(--brand-videoPlayer-closedCaption-fgColor-disabled);
}

@media (forced-colors: active) {
  .VideoPlayer__iconControl svg {
    color: ButtonText;
  }

  .VideoPlayer__iconControl:focus-visible {
    outline-color: Highlight;
  }

  .VideoPlayer__closedCaption:focus-visible {
    outline: none;
  }

  .VideoPlayer__closedCaption:not(.VideoPlayer__ccOff) {
    background: Highlight;
    border-color: Highlight;
  }

  .VideoPlayer__closedCaption:not(.VideoPlayer__ccOff) .VideoPlayer__ccText {
    background-color: Highlight;
    color: HighlightText;
  }
}

/* ---------------------------------------------------------- */
/* 6. Range Element                                           */
/* ---------------------------------------------------------- */
.VideoPlayer__range {
  position: relative;
  width: 100%;
  min-width: var(--brand-VideoPlayer-range-minWidth);
  height: var(--brand-VideoPlayer-range-height);
  background-color: var(--brand-videoPlayer-range-bgColor-default);
  border-radius: var(--brand-VideoPlayer-range-borderRadius);
}

.VideoPlayer__rangeInput {
  font: inherit;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  z-index: 1;
  display: block;
  width: 100%;
  height: var(--brand-VideoPlayer-range-height);
  margin: 0;
  cursor: pointer;
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
}

.VideoPlayer__rangeInput:focus-visible {
  outline: var(--brand-borderWidth-thick) solid var(--brand-color-focus);
  outline-offset: var(--base-size-8);
  border-radius: var(--brand-borderRadius-small);
  opacity: 1;
}

.VideoPlayer__range:hover .VideoPlayer__rangeInput {
  opacity: 1;
}

.VideoPlayer__rangeInput::-moz-range-track,
.VideoPlayer__rangeInput::-webkit-slider-runnable-track {
  background: transparent;
  border: none;
}

.VideoPlayer__rangeProgress {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  overflow: hidden;
  border: 0;
  border-radius: var(--brand-VideoPlayer-range-borderRadius);
  width: 100%;
  position: absolute;
  height: var(--brand-VideoPlayer-range-height);
  top: 0;
  left: 0;
}

.VideoPlayer__rangeProgress::-webkit-progress-bar {
  background-color: var(--brand-videoPlayer-range-bgColor-default);
}

.VideoPlayer__rangeProgress::-webkit-progress-value {
  background-color: var(--brand-videoPlayer-range-bgColor-progress);
}

.VideoPlayer__rangeProgress::-moz-progress-bar {
  background: var(--brand-videoPlayer-range-bgColor-progress);
}

.VideoPlayer__rangeProgress::-ms-fill-lower {
  background-color: var(--brand-videoPlayer-range-bgColor-progress);
  border: none;
}

.VideoPlayer__rangeProgress::-ms-fill-upper {
  background-color: var(--brand-videoPlayer-range-bgColor-default);
  border: none;
}

@media (forced-colors: active) {
  .VideoPlayer__rangeProgress::-webkit-progress-bar {
    background-color: FieldText;
  }

  .VideoPlayer__rangeProgress::-webkit-progress-value {
    background-color: Highlight;
  }

  .VideoPlayer__rangeProgress::-moz-progress-bar {
    background: Highlight;
  }

  .VideoPlayer__rangeProgress::-ms-fill-lower {
    background-color: Highlight;
  }

  .VideoPlayer__rangeProgress::-ms-fill-upper {
    background-color: FieldText;
  }
}

/* ---------------------------------------------------------- */
/* 7. Tooltip                                                 */
/* ---------------------------------------------------------- */

.VideoPlayer__tooltip {
  position: absolute;
  left: 50%;
  bottom: 100%;
  z-index: 2;
  width: var(--base-size-8);
  height: var(--base-size-8);
  margin-bottom: var(--base-size-16);
  opacity: 0;
  transform: translate(-50%, 50%);
}

.VideoPlayer__tooltip::before {
  position: absolute;
  bottom: calc(var(--base-size-4) * -1);
  left: 0;
  width: var(--base-size-12);
  height: var(--base-size-12);
  content: '';
  background: var(--base-color-scale-white-0);
  border-radius: var(--brand-borderRadius-small);
  transform: rotate(45deg);
}

.VideoPlayer__tooltipContent {
  position: absolute;
  bottom: 0;
  left: 50%;
  z-index: -1;
  background: white;
  box-shadow: 0 var(--base-size-4) var(--base-size-4) #1b1f2433;
  transform: translate3d(-50%, 0, 0);
  padding: var(--brand-VideoPlayer-tooltip-padding-block) var(--brand-VideoPlayer-tooltip-padding-inline);
  border-radius: var(--brand-borderRadius-medium);
}

.VideoPlayer__tooltipText {
  color: var(--base-color-scale-gray-9);
}

.VideoPlayer__tooltip-visible {
  opacity: 1;
}

.VideoPlayer__controlTextColor {
  color: var(--base-color-scale-white-0);
}

.VideoPlayer__seekTime {
  white-space: nowrap;
}

.VideoPlayer__container--small .VideoPlayer__title span {
  font-weight: var(--brand-text-weight-300);
  font-size: var(--brand-text-size-300);
  line-height: var(--brand-text-lineHeight-300);
  letter-spacing: var(--brand-text-letterSpacing-300);
}

.VideoPlayer__container--small .VideoPlayer__title svg {
  width: var(--base-size-24);
  height: var(--base-size-24);
}

.VideoPlayer__container--small .VideoPlayer__controls {
  position: initial;
}

/* Remove all animations, transitions and smooth scroll for people that prefer not to see them */
@media (prefers-reduced-motion: reduce) {
  .VideoPlayer,
  .VideoPlayer::before,
  .VideoPlayer::after,
  .VideoPlayer * {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
